import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import MaintenanceNoticeBanner from "@/components/MaintenanceNoticeBanner";
import LandingMobileNav from "@/components/marketing/LandingMobileNav";
import usePublicStatus from "@/hooks/usePublicStatus";
import { API_BASE } from "@/lib/utils";
import { LANDING_DEFAULTS } from "@/lib/landing-defaults";
import { PRODUCT_MENU } from "@/lib/marketing-product-menu";

function mergeNavFooter(remote) {
  if (!remote || typeof remote !== "object") {
    return { nav: LANDING_DEFAULTS.nav, footer: LANDING_DEFAULTS.footer };
  }
  const merged = { ...LANDING_DEFAULTS, ...remote };
  return {
    nav: { ...LANDING_DEFAULTS.nav, ...merged.nav },
    footer: { ...LANDING_DEFAULTS.footer, ...merged.footer },
  };
}

/**
 * Shared marketing header + footer (homepage CMS). Does not affect admin or event pages.
 */
export default function MarketingSiteChrome({ children, contentClassName = "max-w-[1320px] mx-auto px-4 md:px-8" }) {
  const { banners } = usePublicStatus();
  const [nav, setNav] = useState(LANDING_DEFAULTS.nav);
  const [footer, setFooter] = useState(LANDING_DEFAULTS.footer);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/landing`, { cache: "no-store" });
        if (res.ok) {
          const body = await res.json();
          if (!cancelled) {
            const { nav: n, footer: f } = mergeNavFooter(body.content);
            setNav(n);
            setFooter(f);
          }
        }
      } catch {
        // defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen grain flex flex-col overflow-x-clip">
      <MaintenanceNoticeBanner banners={banners} />
      <header className="sticky top-0 z-40 landing-nav-blur border-b border-[var(--border-default)]">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 h-[4.25rem] flex items-center justify-between gap-3 min-w-0">
            <Link to="/" className="shrink min-w-0">
              <Logo accent="#D9F25E" compact />
            </Link>
            <nav className="hidden lg:flex items-center gap-8 text-sm text-[var(--text-secondary)]">
              {(nav?.links || []).map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="hover:text-[var(--text-primary)] transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            <div className="flex items-center gap-2 shrink-0 min-w-0">
              <LandingMobileNav menu={PRODUCT_MENU} links={nav?.links || []} nav={nav} />
              <Link to="/login" className="btn-ghost text-sm hidden lg:inline-flex" data-testid="nav-login">
                {nav?.loginLabel || "Log in"}
              </Link>
              <Link
                to={nav?.trialHref || "/signup"}
                className="btn-secondary text-sm hidden lg:inline-flex"
                data-testid="nav-trial"
              >
                {nav?.trialLabel || "Start free trial"}
              </Link>
              <Link to="/contact?intent=demo" className="btn-primary text-sm whitespace-nowrap" data-testid="nav-launch">
                {nav?.launchLabel || "Book a demo"} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </header>

      <main className="flex-1 pb-16">
        <div className={contentClassName}>{children}</div>
      </main>

      <footer className="border-t border-[var(--border-default)] bg-[var(--bg-base)] py-10 mt-auto">
        <div className="max-w-[1320px] mx-auto px-4 md:px-8 flex flex-wrap justify-between items-center gap-4">
          <Link to="/">
            <Logo accent="#D9F25E" />
          </Link>
          <div className="text-[10px] font-mono text-[var(--text-tertiary)]">{footer?.copyright}</div>
          <div className="flex flex-wrap gap-6 text-sm text-[var(--text-secondary)]">
            {(footer?.links || []).map((l) => (
              <Link key={l.href} to={l.href} className="hover:text-[var(--text-primary)] transition-colors duration-200">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
